import { useMemo } from 'react';
import {
  asBool,
  asNumber,
  asString,
  cloneDoc,
  ensureObject,
  objectMap,
  objectRecord,
  type MonitoringConfigDoc,
} from '../../../lib/monitoring-config-form-utils';
import {
  CheckboxField,
  FieldLabel,
  ItemCard,
  NumberInput,
  RemoveButton,
  SectionBox,
  SubsectionTitle,
  TextInput,
} from '../../tenants/tenant-config-form/FormPrimitives';

interface MasterMonitoringConfigFormProps {
  doc: MonitoringConfigDoc;
  onChange: (doc: MonitoringConfigDoc) => void;
}

function updateDoc(
  onChange: (doc: MonitoringConfigDoc) => void,
  doc: MonitoringConfigDoc,
  mutator: (next: MonitoringConfigDoc) => void,
) {
  const next = cloneDoc(doc);
  mutator(next);
  onChange(next);
}

export function MasterMonitoringConfigForm({ doc, onChange }: MasterMonitoringConfigFormProps) {
  const kuma = objectRecord(doc.kuma);
  const uptime = objectRecord(doc.uptime);
  const uptimeOci = objectRecord(uptime.oci);
  const alertmanager = objectRecord(doc.alertmanager);
  const jira = objectRecord(doc.jira);
  const jiraReporting = objectRecord(jira.reporting);
  const jiraCustomFields = objectRecord(jiraReporting.customFields);
  const jiraCalculations = objectRecord(jiraReporting.calculations);
  const synthetic = objectRecord(doc.synthetic);
  const grafana = objectRecord(doc.grafana);
  const notifications = objectRecord(doc.notifications);
  const teams = objectRecord(notifications.teams);
  const channels = useMemo(() => objectMap(teams.channels), [teams.channels]);
  const channelNames = Object.keys(channels).sort();

  const addChannel = () => {
    const name = window.prompt('Teams channel name (e.g. monitoring-alerts)');
    if (!name?.trim() || channels[name.trim()]) return;
    updateDoc(onChange, doc, (next) => {
      ensureObject(ensureObject(ensureObject(next, 'notifications'), 'teams'), 'channels')[name.trim()] = '';
    });
  };

  return (
    <div className="space-y-4 text-sm">
      <SectionBox title="Uptime Kuma">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FieldLabel label="Base URL">
            <TextInput
              value={asString(kuma.base_url)}
              onChange={(v) => updateDoc(onChange, doc, (n) => { ensureObject(n, 'kuma').base_url = v; })}
              mono
            />
          </FieldLabel>
          <FieldLabel label="API token ref">
            <TextInput
              value={asString(kuma.api_token_ref)}
              onChange={(v) => updateDoc(onChange, doc, (n) => { ensureObject(n, 'kuma').api_token_ref = v; })}
              mono
            />
          </FieldLabel>
        </div>
      </SectionBox>

      <SectionBox title="Uptime runner">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FieldLabel label="OCI bucket">
            <TextInput
              value={asString(uptimeOci.bucket)}
              onChange={(v) =>
                updateDoc(onChange, doc, (n) => {
                  ensureObject(ensureObject(n, 'uptime'), 'oci').bucket = v;
                })
              }
            />
          </FieldLabel>
          <FieldLabel label="OCI prefix">
            <TextInput
              value={asString(uptimeOci.prefix)}
              onChange={(v) =>
                updateDoc(onChange, doc, (n) => {
                  ensureObject(ensureObject(n, 'uptime'), 'oci').prefix = v;
                })
              }
              mono
            />
          </FieldLabel>
          <FieldLabel label="Probe timeout (ms)">
            <NumberInput
              value={asNumber(uptime.timeout_ms, 8000)}
              onChange={(v) => updateDoc(onChange, doc, (n) => { ensureObject(n, 'uptime').timeout_ms = v; })}
            />
          </FieldLabel>
        </div>
      </SectionBox>

      <SectionBox title="Alertmanager">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FieldLabel label="Active alerts API path">
            <TextInput
              value={asString(alertmanager.api_path_active_alerts)}
              onChange={(v) =>
                updateDoc(onChange, doc, (n) => {
                  ensureObject(n, 'alertmanager').api_path_active_alerts = v;
                })
              }
              mono
            />
          </FieldLabel>
          <FieldLabel label="Timeout (ms)">
            <NumberInput
              value={asNumber(alertmanager.timeout_ms, 8000)}
              onChange={(v) => updateDoc(onChange, doc, (n) => { ensureObject(n, 'alertmanager').timeout_ms = v; })}
            />
          </FieldLabel>
        </div>
      </SectionBox>

      <SectionBox title="Jira">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FieldLabel label="Base URL">
            <TextInput
              value={asString(jira.base_url)}
              onChange={(v) => updateDoc(onChange, doc, (n) => { ensureObject(n, 'jira').base_url = v; })}
              mono
            />
          </FieldLabel>
          <FieldLabel label="Username">
            <TextInput
              value={asString(jira.username)}
              onChange={(v) => updateDoc(onChange, doc, (n) => { ensureObject(n, 'jira').username = v; })}
            />
          </FieldLabel>
          <FieldLabel label="API token ref">
            <TextInput
              value={asString(jira.api_token_ref)}
              onChange={(v) => updateDoc(onChange, doc, (n) => { ensureObject(n, 'jira').api_token_ref = v; })}
              mono
            />
          </FieldLabel>
        </div>

        <SubsectionTitle>Reporting custom fields</SubsectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {(['priority', 'outageMinutes', 'category', 'slaTimeToResolution'] as const).map((key) => (
            <FieldLabel key={key} label={key}>
              <TextInput
                value={asString(jiraCustomFields[key])}
                onChange={(v) =>
                  updateDoc(onChange, doc, (n) => {
                    ensureObject(ensureObject(ensureObject(n, 'jira'), 'reporting'), 'customFields')[key] = v;
                  })
                }
                mono
              />
            </FieldLabel>
          ))}
        </div>

        <SubsectionTitle>Reporting calculations</SubsectionTitle>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <FieldLabel label="Trend up threshold">
            <NumberInput
              value={asNumber(jiraCalculations.trendUpThreshold, 0)}
              onChange={(v) =>
                updateDoc(onChange, doc, (n) => {
                  ensureObject(ensureObject(ensureObject(n, 'jira'), 'reporting'), 'calculations').trendUpThreshold = v;
                })
              }
            />
          </FieldLabel>
          <FieldLabel label="Trend down threshold">
            <NumberInput
              value={asNumber(jiraCalculations.trendDownThreshold, 0)}
              onChange={(v) =>
                updateDoc(onChange, doc, (n) => {
                  ensureObject(ensureObject(ensureObject(n, 'jira'), 'reporting'), 'calculations').trendDownThreshold = v;
                })
              }
            />
          </FieldLabel>
          <FieldLabel label="SLA breach tolerance">
            <NumberInput
              value={asNumber(jiraCalculations.slaBreachTolerance, 0)}
              onChange={(v) =>
                updateDoc(onChange, doc, (n) => {
                  ensureObject(ensureObject(ensureObject(n, 'jira'), 'reporting'), 'calculations').slaBreachTolerance = v;
                })
              }
            />
          </FieldLabel>
        </div>
        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-2">
          Jira reporting JQL templates are edited in the YAML tab.
        </p>
      </SectionBox>

      <SectionBox title="Synthetic monitoring">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <FieldLabel label="Base URL">
            <TextInput
              value={asString(synthetic.base_url)}
              onChange={(v) => updateDoc(onChange, doc, (n) => { ensureObject(n, 'synthetic').base_url = v; })}
              mono
            />
          </FieldLabel>
          <FieldLabel label="API token ref">
            <TextInput
              value={asString(synthetic.api_token_ref)}
              onChange={(v) => updateDoc(onChange, doc, (n) => { ensureObject(n, 'synthetic').api_token_ref = v; })}
              mono
            />
          </FieldLabel>
        </div>
      </SectionBox>

      <SectionBox title="Grafana">
        <FieldLabel label="Webhook secret ref">
          <TextInput
            value={asString(grafana.webhook_secret_ref)}
            onChange={(v) => updateDoc(onChange, doc, (n) => { ensureObject(n, 'grafana').webhook_secret_ref = v; })}
            mono
          />
        </FieldLabel>
      </SectionBox>

      <SectionBox title="Teams notifications" onAdd={addChannel} addLabel="Add channel">
        <CheckboxField
          label="Teams notifications enabled"
          checked={asBool(teams.enabled)}
          onChange={(checked) =>
            updateDoc(onChange, doc, (n) => {
              ensureObject(ensureObject(n, 'notifications'), 'teams').enabled = checked;
            })
          }
        />
        {channelNames.length === 0 ? (
          <p className="text-xs text-gray-500">No Teams channels defined.</p>
        ) : (
          channelNames.map((channelName) => (
            <ItemCard key={channelName}>
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold font-mono">{channelName}</span>
                <RemoveButton
                  label={`Remove channel ${channelName}`}
                  onClick={() =>
                    updateDoc(onChange, doc, (n) => {
                      delete ensureObject(ensureObject(ensureObject(n, 'notifications'), 'teams'), 'channels')[channelName];
                    })
                  }
                />
              </div>
              <FieldLabel label="Webhook URL ref">
                <TextInput
                  value={asString(channels[channelName])}
                  onChange={(v) =>
                    updateDoc(onChange, doc, (n) => {
                      ensureObject(ensureObject(ensureObject(n, 'notifications'), 'teams'), 'channels')[channelName] = v;
                    })
                  }
                  mono
                />
              </FieldLabel>
            </ItemCard>
          ))
        )}
      </SectionBox>

      <p className="text-[11px] text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700 pt-3">
        Unlisted fields (Jira JQL templates, etc.) are preserved when saving from the form and can be edited in the YAML tab.
      </p>
    </div>
  );
}
